import React from 'react';

const UniversalInsuranceLoader = ({ 
  type = 'car', 
  title = 'Loading Insurance Details',
  subtitle = 'Getting the best quotes for you...',
  theme = 'blue' 
}) => {
  
  // Theme configurations
  const themes = {
    blue: {
      gradient: 'from-blue-50 to-indigo-100',
      primary: '#3B82F6',
      secondary: '#1E40AF',
      accent: '#FBBF24'
    },
    green: {
      gradient: 'from-green-50 to-emerald-100',
      primary: '#10B981',
      secondary: '#047857',
      accent: '#F59E0B'
    },
    purple: {
      gradient: 'from-purple-50 to-violet-100',
      primary: '#8B5CF6',
      secondary: '#5B21B6',
      accent: '#EF4444'
    },
    orange: {
      gradient: 'from-orange-50 to-amber-100',
      primary: '#F97316',
      secondary: '#EA580C',
      accent: '#06B6D4'
    }
  };

  const currentTheme = themes[theme] || themes.blue;

  // Icon configurations for different insurance types
  const getInsuranceIcon = () => {
    switch(type) {
      case 'car':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="insurance-icon">
            <rect x="10" y="25" width="50" height="15" rx="3" fill={currentTheme.primary} className="main-shape"/>
            <path d="M20 25 L25 15 L45 15 L50 25 Z" fill={currentTheme.secondary} className="secondary-shape"/>
            <rect x="22" y="17" width="8" height="6" rx="1" fill="#E0F2FE" className="detail"/>
            <rect x="35" y="17" width="8" height="6" rx="1" fill="#E0F2FE" className="detail"/>
            <circle cx="22" cy="37" r="6" fill="#374151" className="wheel wheel-1"/>
            <circle cx="48" cy="37" r="6" fill="#374151" className="wheel wheel-2"/>
            <circle cx="22" cy="37" r="3" fill="#6B7280"/>
            <circle cx="48" cy="37" r="3" fill="#6B7280"/>
            <circle cx="62" cy="30" r="2" fill={currentTheme.accent} className="highlight"/>
          </svg>
        );
      
      case 'health':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="insurance-icon">
            <rect x="20" y="10" width="40" height="30" rx="5" fill={currentTheme.primary} className="main-shape"/>
            <rect x="35" y="18" width="10" height="4" fill="white" className="detail"/>
            <rect x="37" y="16" width="6" height="8" fill="white" className="detail"/>
            <circle cx="30" cy="35" r="2" fill={currentTheme.accent} className="highlight pulse-highlight"/>
            <circle cx="40" cy="30" r="1.5" fill={currentTheme.accent} className="highlight pulse-highlight"/>
            <circle cx="50" cy="35" r="2" fill={currentTheme.accent} className="highlight pulse-highlight"/>
          </svg>
        );
      
      case 'life':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="insurance-icon">
            <circle cx="40" cy="20" r="8" fill={currentTheme.primary} className="main-shape"/>
            <path d="M32 28 Q40 35 48 28 L45 40 L35 40 Z" fill={currentTheme.secondary} className="secondary-shape"/>
            <circle cx="40" cy="20" r="4" fill="white" className="detail"/>
            <rect x="38" y="30" width="4" height="8" fill={currentTheme.accent} className="highlight"/>
          </svg>
        );
      
      case 'bike':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="insurance-icon">
            <path d="M20 25 L40 10 L60 25 L60 40 L20 40 Z" fill={currentTheme.primary} className="main-shape"/>
            <rect x="30" y="30" width="8" height="10" fill={currentTheme.secondary} className="secondary-shape"/>
            <rect x="32" y="32" width="4" height="4" fill={currentTheme.accent} className="detail"/>
            <rect x="45" y="20" width="6" height="6" fill="white" className="detail"/>
            <circle cx="55" cy="35" r="2" fill={currentTheme.accent} className="highlight pulse-highlight"/>
          </svg>
        );
      
      case 'travel':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="insurance-icon">
            <path d="M15 30 L25 15 L55 15 L65 30 L60 35 L20 35 Z" fill={currentTheme.primary} className="main-shape"/>
            <rect x="30" y="20" width="20" height="8" fill="white" className="detail"/>
            <circle cx="25" cy="37" r="3" fill="#374151" className="wheel wheel-1"/>
            <circle cx="55" cy="37" r="3" fill="#374151" className="wheel wheel-2"/>
            <rect x="35" y="22" width="2" height="4" fill={currentTheme.accent} className="highlight"/>
            <rect x="43" y="22" width="2" height="4" fill={currentTheme.accent} className="highlight"/>
          </svg>
        );
      
      default:
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="insurance-icon">
            <rect x="20" y="15" width="40" height="20" rx="5" fill={currentTheme.primary} className="main-shape"/>
            <circle cx="40" cy="25" r="8" fill="white" className="detail"/>
            <path d="M35 25 L38 28 L45 21" stroke={currentTheme.secondary} strokeWidth="2" fill="none" className="highlight"/>
          </svg>
        );
    }
  };

  // Get floating icons based on insurance type
  const getFloatingIcons = () => {
    const iconSets = {
      car: ['🚗', '🛡️', '💰', '📋'],
      health: ['🏥', '💊', '❤️', '🩺'],
      life: ['👨‍👩‍👧‍👦', '🏠', '💰', '📜'],
      bike: ['📋', '🔒', '🛡️', '🔥'],
      travel: ['✈️', '🧳', '🌍', '📱'],
      default: ['🛡️', '💰', '📋', '✅']
    };
    
    return iconSets[type] || iconSets.default;
  };

  const floatingIcons = getFloatingIcons();

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${currentTheme.gradient}`}>
      <div className="relative">
        {/* Main container */}
        <div className="relative bg-white rounded-2xl shadow-2xl p-8 w-80 h-64 flex flex-col items-center justify-center">
          
          {/* Animated insurance icon */}
          <div className="relative mb-6">
            <div className="icon-container">
              {getInsuranceIcon()}
            </div>
            
            {/* Progress bar for non-car types */}
            {type !== 'car' && (
              <div className="progress-bar-container mt-4">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ backgroundColor: currentTheme.primary }}></div>
                </div>
              </div>
            )}
            
            {/* Road animation for car type */}
            {type === 'car' && (
              <div className="road-container mt-4">
                <div className="road-line" style={{ backgroundColor: currentTheme.accent }}></div>
                <div className="road-line road-line-2" style={{ backgroundColor: currentTheme.accent }}></div>
                <div className="road-line road-line-3" style={{ backgroundColor: currentTheme.accent }}></div>
              </div>
            )}
          </div>
          
          {/* Loading text */}
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              <span className="typing-text">{title}</span>
              <span className="cursor">|</span>
            </h3>
            <p className="text-gray-600 text-sm">{subtitle}</p>
          </div>
          
          {/* Progress dots */}
          <div className="flex space-x-2 mt-4">
            <div className="dot dot-1" style={{ backgroundColor: currentTheme.primary }}></div>
            <div className="dot dot-2" style={{ backgroundColor: currentTheme.primary }}></div>
            <div className="dot dot-3" style={{ backgroundColor: currentTheme.primary }}></div>
            <div className="dot dot-4" style={{ backgroundColor: currentTheme.primary }}></div>
          </div>
        </div>
        
        {/* Floating insurance icons */}
        {floatingIcons.map((icon, index) => (
          <div key={index} className={`floating-icon icon-${index + 1}`}>
            {icon}
          </div>
        ))}
      </div>
      
      <style jsx>{`
        .icon-container {
          animation: bounce 2s ease-in-out infinite;
        }
        
        .main-shape {
          animation: pulse 1.5s ease-in-out infinite;
        }
        
        .secondary-shape {
          animation: pulse 2s ease-in-out infinite;
        }
        
        .wheel {
          animation: spin 1s linear infinite;
          transform-origin: center;
        }
        
        .highlight, .pulse-highlight {
          animation: blink 2s ease-in-out infinite;
        }
        
        .pulse-highlight {
          animation: pulseGlow 1.5s ease-in-out infinite;
        }
        
        .road-container {
          height: 4px;
          background: #374151;
          width: 120px;
          position: relative;
          border-radius: 2px;
          overflow: hidden;
        }
        
        .road-line {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          height: 2px;
          width: 20px;
          border-radius: 1px;
          animation: roadMove 1s linear infinite;
        }
        
        .road-line-2 {
          animation-delay: -0.3s;
        }
        
        .road-line-3 {
          animation-delay: -0.6s;
        }
        
        .progress-bar-container {
          width: 120px;
        }
        
        .progress-bar {
          height: 4px;
          background: #E5E7EB;
          border-radius: 2px;
          overflow: hidden;
        }
        
        .progress-fill {
          height: 100%;
          width: 0%;
          border-radius: 2px;
          animation: progressFill 2s ease-in-out infinite;
        }
        
        .typing-text {
          animation: typewriter 3s steps(25) infinite;
        }
        
        .cursor {
          animation: blink 1s step-end infinite;
        }
        
        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: dotPulse 1.4s ease-in-out infinite both;
        }
        
        .dot-2 { animation-delay: -0.32s; }
        .dot-3 { animation-delay: -0.16s; }
        .dot-4 { animation-delay: 0s; }
        
        .floating-icon {
          position: absolute;
          font-size: 24px;
          animation: float 3s ease-in-out infinite;
        }
        
        .icon-1 {
          top: -20px;
          left: -20px;
          animation-delay: 0s;
        }
        
        .icon-2 {
          top: -20px;
          right: -20px;
          animation-delay: -1s;
        }
        
        .icon-3 {
          bottom: -20px;
          left: -20px;
          animation-delay: -2s;
        }
        
        .icon-4 {
          bottom: -20px;
          right: -20px;
          animation-delay: -1.5s;
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0) scale(1);
          }
          40% {
            transform: translateY(-10px) scale(1.05);
          }
          60% {
            transform: translateY(-5px) scale(1.02);
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes pulseGlow {
          0%, 100% { 
            opacity: 1; 
            transform: scale(1);
            filter: brightness(1);
          }
          50% { 
            opacity: 0.7; 
            transform: scale(1.2);
            filter: brightness(1.5);
          }
        }
        
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        
        @keyframes roadMove {
          0% { left: -30px; }
          100% { left: 130px; }
        }
        
        @keyframes progressFill {
          0% { width: 0%; }
          70% { width: 100%; }
          100% { width: 100%; }
        }
        
        @keyframes typewriter {
          0% { width: 0; }
          50% { width: 100%; }
          100% { width: 0; }
        }
        
        @keyframes dotPulse {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          33% {
            transform: translateY(-10px) rotate(5deg);
          }
          66% {
            transform: translateY(-5px) rotate(-5deg);
          }
        }
      `}</style>
    </div>
  );
};

export default UniversalInsuranceLoader;